/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1726790400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Sep 20 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "redis",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Redis",
          "depth": 0,
          "id": "connect-to-redis"
        }, {
          "value": "Connect to a Redis instance",
          "depth": 1,
          "id": "connect-to-a-redis-instance"
        }, {
          "value": "Connect to a Redis cluster",
          "depth": 1,
          "id": "connect-to-a-redis-cluster"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing to a cache database",
          "depth": 1,
          "id": "syncing-to-a-cache-database"
        }, {
          "value": "Syncing keys",
          "depth": 1,
          "id": "syncing-keys"
        }, {
          "value": "Syncing members",
          "depth": 1,
          "id": "syncing-members"
        }, {
          "value": "Transactions",
          "depth": 1,
          "id": "transactions"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      h3: "h3",
      h4: "h4",
      code: "code",
      em: "em",
      img: "img"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Redis Cache"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to a Redis cache database"
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://redis.io/docs/manual/client-side-caching/",
                children: "Redis Cache"
              })
            })]
          })
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-redis",
        children: "Connect to Redis"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Redis"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-a-redis-instance",
        children: "Connect to a Redis instance"
      }), "\n", _jsx(_components.p, {
        children: "Authenticate Hightouch to Redis with your hostname and port. If your Redis instance has authentication credentials, you can also input a username and password."
      }), "\n", _jsx(_components.h4, {
        id: "ssh-tunneling",
        children: "SSH tunneling"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can connect directly to Redis over the public internet or via an SSH tunnel. Since data is encrypted in transit via TLS, a direct connection is suitable for most use cases. You may need to set up a tunnel if your Redis instance is on a private network or virtual private cloud (VPC)."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports both standard and reverse SSH tunnels. To learn more about SSH tunneling, refer to Hightouch's ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "tunneling documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-a-redis-cluster",
        children: "Connect to a Redis cluster"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch needs permission to access all cluster nodes of your Redis cluster. If your Redis cluster exists on a publicly available network, only the primary cluster node URL is required. Provide the cluster node URLs in the specified ", _jsx(_components.code, {
          children: "redis://<host>:<port>"
        }), " format."]
      }), "\n", _jsx(_components.h4, {
        id: "node-address-map",
        children: "Node address map"
      }), "\n", _jsxs(_components.p, {
        children: ["A node address map is a mapping between the addresses in the cluster and the addresses the client should connect to. This is useful when connecting to a cluster running on a different network than the client. For more information on node address maps specifically, refer to ", _jsxs(_components.a, {
          href: "https://github.com/redis/node-redis/blob/master/docs/clustering.md#node-address-map",
          children: [_jsx(_components.code, {
            children: "node-redis"
          }), "' documentation"]
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To get the addresses of your Redis cluster nodes, run ", _jsx(_components.code, {
          children: "CLUSTER NODES"
        }), " in your cluster. Refer to the ", _jsx(_components.a, {
          href: "https://redis.io/docs/latest/commands/cluster-nodes/",
          children: "Redis documentation"
        }), " for more details."]
      }), "\n", _jsxs(_components.p, {
        children: ["A common use case of node address mapping is connecting to AWS ElastiCache. To access ElastiCache resources through Hightouch, you will need to follow the instructions defined ", _jsx(_components.a, {
          href: "https://docs.aws.amazon.com/AmazonElastiCache/latest/red-ug/accessing-elasticache.html",
          children: "in this AWS tutorial"
        }), " to create a NAT instance in the same VPC as your ElastiCache instance, but on a public subnet. This NAT instance will need to forward a port to each Redis cluster node. Ensure that you can successfully connect to your Redis cluster through the NAT instance."]
      }), "\n", _jsx(_components.p, {
        children: "When configuring your connection from Hightouch, construct the Redis cluster node URLs by using the publicly accessible IP address of your NAT instance with the corresponding ports. Then, map each cluster node address to the corresponding public address in the node address mapping section."
      }), "\n", _jsx(_components.h4, {
        id: "connect-over-a-private-network",
        children: "Connect over a private network"
      }), "\n", _jsxs(_components.p, {
        children: ["If you have a Redis cluster running on a private network with no publicly accessible endpoints, you will need to create a ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "SSH tunnel"
        }), " to ", _jsx(_components.em, {
          children: "each"
        }), " cluster node."]
      }), "\n", _jsxs(_components.p, {
        children: ["To get the addresses of your Redis cluster nodes, run ", _jsx(_components.code, {
          children: "CLUSTER NODES"
        }), " in your cluster. Refer to the ", _jsx(_components.a, {
          href: "https://redis.io/docs/latest/commands/cluster-nodes/",
          children: "Redis documentation"
        }), " for more details.\nProvide the URL for each cluster node, and map it to its corresponding SSH tunnel."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Redis destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Redis destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-to-a-cache-database",
        children: "Syncing to a cache database"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports syncing ", _jsx(_components.a, {
          href: "#syncing-keys",
          children: _jsx(_components.strong, {
            children: "keys"
          })
        }), " and ", _jsx(_components.a, {
          href: "#syncing-members",
          children: _jsx(_components.strong, {
            children: "members"
          })
        }), " to Redis."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-keys",
        children: "Syncing keys"
      }), "\n", _jsx(_components.p, {
        children: "Keys identify, store, and retrieve data. Each key in Redis maps to a corresponding value."
      }), "\n", _jsx(_components.h4, {
        id: "supported-commands",
        children: "Supported commands"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Command"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.code, {
                  children: "SET"
                })
              })
            }), _jsx(_components.td, {
              children: "Set key to hold the string value."
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://redis.io/commands/set/",
                children: "SET Command"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.code, {
                  children: "HSET"
                })
              })
            }), _jsx(_components.td, {
              children: "Set specified fields to their respective values in the hash stored at key."
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://redis.io/commands/hset/",
                children: "HSET Command"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.code, {
                  children: "SADD"
                })
              })
            }), _jsx(_components.td, {
              children: "Add specified members to the set stored at key."
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://redis.io/commands/sadd/",
                children: "SADD Command"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.code, {
                  children: "GEOADD"
                })
              })
            }), _jsx(_components.td, {
              children: "Add specified geospatial items (longitude, latitude, name) to the specified key."
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://redis.io/commands/geoadd/",
                children: "GEOADD Command"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to records in Redis, you need to select the model column that contains values that match the ", _jsx(_components.strong, {
          children: "Key"
        }), " field in Redis."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to custom fields in Redis."
      }), "\n", _jsx(_components.h4, {
        id: "time-to-live-ttl",
        children: "Time to live (TTL)"
      }), "\n", _jsxs(_components.p, {
        children: ["You can set an expiration for the keys that you are syncing to Redis by setting a ", _jsx(_components.a, {
          href: "https://redis.io/commands/ttl/",
          children: "TTL value"
        }), ". This value is the amount of time in seconds that the keys will exist in Redis before they're automatically deleted."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the key in Redis with its fields untouched"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Keep the key in Redis, but set mapped fields to empty"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Remove the key from Redis"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-members",
        children: "Syncing members"
      }), "\n", _jsxs(_components.p, {
        children: ["Members are used for managing ", _jsx(_components.a, {
          href: "https://redis.io/docs/data-types/sorted-sets/",
          children: "sorted sets"
        }), " in Redis."]
      }), "\n", _jsx(_components.h4, {
        id: "supported-commands-1",
        children: "Supported commands"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Command"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.code, {
                  children: "ZADD"
                })
              })
            }), _jsx(_components.td, {
              children: "Add all specified members with the specified scores to the sorted set stored at key."
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://redis.io/commands/zadd/",
                children: "ZADD Command"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.code, {
                  children: "GEOADD"
                })
              })
            }), _jsx(_components.td, {
              children: "Add specified geospatial items (longitude, latitude, name) to the specified key."
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://redis.io/commands/geoadd/",
                children: "GEOADD Command"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to records in Redis, select the model column that contains the unique member values of each element of the sorted set in Redis."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["The fields available for mapping depend on the ", _jsx(_components.a, {
          href: "#supported-commands-1",
          children: "command"
        }), " that you've selected."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior-1",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the member in Redis with its fields untouched"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Keep the member in Redis, but set mapped fields to empty"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Remove the member from the sorted set from Redis"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "transactions",
        children: "Transactions"
      }), "\n", _jsx(_components.p, {
        children: "Transactions are disabled by default when connected to a Redis cluster.\nYou can toggle transactions for your Redis instance by configuring the options as shown."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destinations-redis-cluster-support.png",
          alt: "Disabling transactions in Hightouch"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "invalid-argument-type",
        children: "Invalid argument type"
      }), "\n", _jsxs(_components.p, {
        children: ["This error can happen when syncing data types that aren't ", _jsx(_components.a, {
          href: "https://redis.io/docs/data-types/",
          children: "supported by Redis"
        }), ", such as ", _jsx(_components.code, {
          children: "null"
        }), " values. If your model columns contain ", _jsx(_components.code, {
          children: "null"
        }), " values, you can enable ", _jsx(_components.a, {
          href: "/syncs/mapping-data#dont-sync-null-values",
          children: "Don't sync null values"
        }), " to avoid syncing them."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
